import type { ClientEvent, UserContent } from '@oneapp/shared';
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { SIGN_IN_REFUSED_EVENT } from '../auth/AuthProvider';
import { ApiError, isSignInRefused } from '../lib/api';
import { chatApi } from './api';
import {
  type ChatAction,
  type Closing,
  chatReducer,
  initialChatState,
  type Outgoing,
  openQuestions,
  type SendError,
} from './state';
import { type ChatStream, openChatStream } from './stream';

/** "Still working on your last message": wait and send again this many times before giving up. */
const BUSY_RETRIES = 3;
const NOTICE_MS = 6_000;
/** A send still going after this long no longer holds back the one after it (the server then says it's busy). */
const IN_ORDER_MS = 30_000;
/** A typing indicator left on (a dropped update) goes away by itself after this long. */
const TYPING_MS = 20_000;

const refuseSignIn = () => window.dispatchEvent(new Event(SIGN_IN_REFUSED_EVENT));
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * One conversation, live: loads it (the latest one when no id is given), follows the server's
 * updates, and sends taps and messages in the order they were made, showing them before the server confirms.
 * Failed sends can be retried; retries reuse the same id, so the server handles each event once.
 */
export function useConversation(conversationId: string | undefined) {
  const [state, dispatch] = useReducer(chatReducer, undefined, initialChatState);
  const [attempt, setAttempt] = useState(0);
  const [invalidCode, setInvalidCode] = useState<string | null>(null);
  // Volatile retry values never enter the chat reducer, optimistic messages or telemetry.
  const codes = useRef(new Map<string, string>());
  const latest = useRef(state);
  latest.current = state;
  // The conversation on screen; replies to an earlier one are ignored.
  const current = useRef<string | null>(null);
  const stream = useRef<ChatStream | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `attempt` is how "Try again" re-runs this.
  useEffect(() => {
    let alive = true;
    let live: ChatStream | null = null;
    // The newest revision the stream has delivered: where it resumes after a reconnect.
    let streamRev = 0;
    const act = (action: ChatAction) => {
      if (alive) dispatch(action);
    };
    dispatch({ type: 'reset' });
    current.current = null;
    codes.current.clear();

    (async () => {
      try {
        // Opening "my chat" returns its messages too: one round trip on a slow connection.
        const snapshot = conversationId ? await chatApi.snapshot(conversationId) : await chatApi.start();
        if (!alive) return;
        const id = snapshot.conversation.id;
        current.current = id;
        streamRev = snapshot.rev;
        act({ type: 'snapshot', snapshot });
        live = openChatStream({
          conversationId: id,
          after: () => streamRev,
          onEvent: (event) => {
            if (!alive) return;
            if (event.type === 'message') {
              streamRev = Math.max(streamRev, event.message.rev);
              act({ type: 'messages', messages: [event.message] });
            } else if (event.type === 'progress') {
              act({ type: 'progress', text: event.text });
            } else if (event.type === 'typing') {
              act({ type: 'typing', on: event.on });
            } else if (event.type === 'conversation') {
              act({ type: 'conversation', conversation: event.conversation });
            } else {
              act({ type: 'ended', reason: event.reason });
              if (event.reason === 'signed_out') refuseSignIn();
            }
          },
          onConnection: (connection) => act({ type: 'connection', connection }),
          onSignInRefused: refuseSignIn,
        });
        stream.current = live;
      } catch (error) {
        if (!alive) return;
        if (isSignInRefused(error)) return refuseSignIn();
        const reason =
          error instanceof ApiError && error.status === 404
            ? 'notFound'
            : error instanceof ApiError && error.status !== 0
              ? 'server'
              : 'offline';
        act({ type: 'loadFailed', reason });
      }
    })();

    return () => {
      alive = false;
      live?.close();
      stream.current = null;
      current.current = null;
    };
  }, [conversationId, attempt]);

  /** Brings the screen in line with the server (after a question moved on without us). */
  const resync = useCallback(async () => {
    const id = current.current;
    if (!id) return;
    try {
      const snapshot = await chatApi.snapshot(id);
      if (current.current === id) dispatch({ type: 'snapshot', snapshot });
    } catch {
      // The live updates will catch up.
    }
  }, []);

  const deliver = useCallback(
    async (outgoing: Outgoing) => {
      const id = current.current;
      if (!id) return;
      for (let tries = 0; ; tries++) {
        try {
          if (outgoing.event.type === 'code') {
            const value = codes.current.get(outgoing.clientId);
            if (value === undefined) return;
            const response = await chatApi.code(
              outgoing.event.challengeId,
              value,
              outgoing.clientId,
              outgoing.event.from,
            );
            codes.current.delete(outgoing.clientId);
            if (current.current !== id) return;
            if (response.result === 'invalid_format') {
              setInvalidCode(outgoing.event.challengeId);
              dispatch({ type: 'discard', clientId: outgoing.clientId });
            } else {
              await resync();
              dispatch({ type: 'sent', clientId: outgoing.clientId });
            }
            return;
          }
          const result = await chatApi.send(id, outgoing.event, outgoing.clientId);
          if (current.current !== id) return;
          dispatch({ type: 'sent', clientId: outgoing.clientId });
          dispatch({ type: 'messages', messages: result.messages });
          return;
        } catch (error) {
          if (current.current !== id) return;
          if (isSignInRefused(error)) return refuseSignIn();
          const status = error instanceof ApiError ? error.status : 0;
          const code = error instanceof ApiError ? error.problem?.code : undefined;
          if (code === 'QUESTION_CLOSED' || (outgoing.event.type === 'code' && status === 404)) {
            codes.current.delete(outgoing.clientId);
            dispatch({ type: 'discard', clientId: outgoing.clientId, notice: 'movedOn' });
            return void resync();
          }
          if (status === 404) return dispatch({ type: 'ended', reason: 'deleted' });
          const busy = code === 'CHAT_BUSY' || code === 'IDEMPOTENCY_IN_PROGRESS';
          if (busy && tries < BUSY_RETRIES) {
            const wait = error instanceof ApiError ? (error.problem?.retryAfterSec ?? 2) : 2;
            await sleep(wait * 1000);
            continue;
          }
          const reason: SendError =
            status === 0 ? 'offline' : status === 429 ? 'rateLimited' : busy ? 'busy' : 'server';
          return dispatch({ type: 'sendFailed', clientId: outgoing.clientId, error: reason });
        }
      }
    },
    [resync],
  );

  // Each send waits for the one before it: a tap and a message typed straight after it went out together, and the
  // message could reach the server first and be answered at the wrong step.
  const queue = useRef<Promise<void>>(Promise.resolve());
  const enqueue = useCallback(
    (outgoing: Outgoing) => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      const stuck = new Promise<void>((resolve) => {
        timer = setTimeout(resolve, IN_ORDER_MS);
      });
      const mine = Promise.race([queue.current, stuck]).then(async () => {
        clearTimeout(timer);
        await deliver(outgoing);
      });
      queue.current = mine.catch(() => {});
    },
    [deliver],
  );

  /** Send a tap or a message. `content` is how it shows in the chat until the server's copy arrives. */
  const send = useCallback(
    (event: ClientEvent, content: UserContent) => {
      // The server closes every open question when anything is sent; a tap records its choice.
      const closes: Closing[] = openQuestions(latest.current).map((m) =>
        event.type === 'choice' && m.id === event.messageId
          ? { messageId: m.id, selected: event.choiceId }
          : { messageId: m.id },
      );
      const outgoing: Outgoing = {
        clientId: crypto.randomUUID(),
        event,
        content,
        state: 'sending',
        closes,
        at: new Date().toISOString(),
      };
      dispatch({ type: 'send', outgoing });
      enqueue(outgoing);
    },
    [enqueue],
  );

  const sendCode = useCallback(
    (challengeId: string, value: string, summary = '••••••', from: 'page' | 'box' = 'box') => {
      const clientId = crypto.randomUUID();
      codes.current.set(clientId, value);
      setInvalidCode(null);
      const outgoing: Outgoing = {
        clientId,
        event: { type: 'code', challengeId, from },
        content: { type: 'user_submit', summary, icon: 'lock' },
        state: 'sending',
        closes: [],
        at: new Date().toISOString(),
      };
      dispatch({ type: 'send', outgoing });
      enqueue(outgoing);
    },
    [enqueue],
  );

  const retry = useCallback(
    (clientId: string) => {
      const outgoing = latest.current.outgoing.find((o) => o.clientId === clientId && o.state === 'failed');
      if (!outgoing) return;
      dispatch({ type: 'retry', clientId });
      enqueue({ ...outgoing, state: 'sending' });
    },
    [enqueue],
  );

  const discard = useCallback((clientId: string) => {
    codes.current.delete(clientId);
    dispatch({ type: 'discard', clientId });
  }, []);

  const loadOlder = useCallback(async () => {
    const id = current.current;
    const { hasMore, loadingOlder, messages } = latest.current;
    const first = messages[0];
    if (!id || !hasMore || loadingOlder || !first) return;
    dispatch({ type: 'olderLoading' });
    try {
      const page = await chatApi.older(id, first.seq);
      if (current.current === id) dispatch({ type: 'older', messages: page.messages, hasMore: page.hasMore });
    } catch (error) {
      if (isSignInRefused(error)) refuseSignIn();
      dispatch({ type: 'olderFailed' });
    }
  }, []);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  // Messages that failed while offline go again once the connection is back.
  useEffect(() => {
    const onOnline = () => {
      for (const o of latest.current.outgoing) if (o.state === 'failed' && o.error === 'offline') retry(o.clientId);
      stream.current?.reconnectNow();
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [retry]);

  useEffect(() => {
    if (!state.notice) return;
    const timer = setTimeout(() => dispatch({ type: 'clearNotice' }), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [state.notice]);

  useEffect(() => {
    if (!state.typing) return;
    const timer = setTimeout(() => dispatch({ type: 'typing', on: false }), TYPING_MS);
    return () => clearTimeout(timer);
  }, [state.typing]);

  return { state, send, sendCode, invalidCode, retry, discard, loadOlder, reload };
}
